import { Injectable } from '@angular/core';
import { Selector, State, StateContext, Action, Store } from '@ngxs/store';
import { BorrowerState } from 'ng2-app/store/borrower/borrower/borrower.state';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import {
  GetBorrowerSidebarCards,
  UpdateCardsStorage, UpdateExpandedStatus
} from 'ng2-app/borrower-sidebar/borrower-sidebar.actions';

export interface BorrowerSidebarCard {
  name: string;
  shouldShow: boolean;
  expanded: boolean;
  previewIcon: string;
}

export class BorrowerSidebarStateModel {
  cards: BorrowerSidebarCard[];
}

@State<BorrowerSidebarStateModel>({
  name: 'borrowerSidebar',
  defaults: {
    cards: []
  }
})

@Injectable()
export class BorrowerSidebarState {

  @Selector()
  static activeCards(state: BorrowerSidebarStateModel) {
    return state.cards.filter( card => card.shouldShow );
  }

  @Selector()
  static cards(state: BorrowerSidebarStateModel) {
    return state.cards;
  }

  permissions = [
    'canUseAdvancedFDR',
    'canViewFdrDnc',
    'canViewFundingDeskApplicationReview',
    'canViewFundingDeskDocumentValidation',
    'canViewFundingDeskSidebarPotentialDeals',
    'canViewFundingDeskSubmissionSnapshot',
    'hsDisableStips',
    'useAppRequirements',
  ];

  cardConfig = {
    fundingDeskRequest: {
      showCondition: ( { permissions, borrower } ) => {
        const validStatus = borrower &&
          borrower.stage !== 'inactive' &&
          ![
            'noAttempt',
            'attempted',
            'funded',
          ].includes(borrower?.status);

        return validStatus || permissions.canViewFdrDnc;

      },
      expandCondition: ({ permissions }) => permissions.canUseAdvancedFDR,
      previewIcon: 'library_add',
    },
    fundingDeskApplicationReview: {
      showCondition: ( { permissions } ) => permissions.canViewFundingDeskApplicationReview,
      expandCondition: () => true,
      previewIcon: 'playlist_add_check',
    },
    stipulations: {
      showCondition: ( { permissions } ) => permissions.hsDisableStips,
      expandCondition: () => true,
      previewIcon: 'format_list_numbered_rtl',
    },
    potentialDeals: {
      showCondition: ( { permissions } ) => permissions.canViewFundingDeskSidebarPotentialDeals,
      expandCondition: () => true,
      previewIcon: 'file_open',
    },
    submissionSnapshot: {
      showCondition: ( { permissions } ) => permissions.canViewFundingDeskSubmissionSnapshot,
      expandCondition: () => false,
      previewIcon: 'photo_camera',
    },
    notes: {
      showCondition: () => true,
      expandCondition: () => true,
      previewIcon: 'speaker_notes',
    },
    appRequirements: {
      showCondition: ( { permissions, borrower } ) => {
        const validStatus = borrower &&
          [
            'noAttempt',
            'attempted',
            'inContact',
            'dealInProgress',
            'docPrep'
          ].includes(borrower?.status);

        return validStatus || permissions.useAppRequirements;

      },
      expandCondition: () => true,
      previewIcon: 'checklist_rtl',
    },
  };

  constructor(
    private store: Store,
  ) {}

  @Action(GetBorrowerSidebarCards)
  getBorrowerSidebarCards(
    { patchState }: StateContext<BorrowerSidebarStateModel>,
    { }: GetBorrowerSidebarCards
  ) {
    const params = {
      borrower: this.store.selectSnapshot( BorrowerState.borrower ),
      permissions: this.store.selectSnapshot( LenderUserState.hasPermissions(this.permissions) )
    };
    const cardsStorage = localStorage.getItem('borrowerSidebarCards');
    let cards;
    try {
      // @ts-ignore
      cards = this.configureCards(params, JSON.parse(cardsStorage));
    } catch ( e ) {
      console.dir('Error parsing borrower sidebar config storage. Reverting to defaults.');
      console.dir(e);
      cards = this.defaultCardsConfig(params);
      this.setCardsStorage(cards);
    }

    patchState({cards});
  }

  @Action(UpdateCardsStorage)
  updateCardsOrderStorage(
    { getState }: StateContext<BorrowerSidebarStateModel>
  ) {
    this.setCardsStorage(getState().cards);
  }

  @Action(UpdateExpandedStatus)
  updateExpandedStatus(
    { getState }: StateContext<BorrowerSidebarStateModel>,
    { name, expanded }: UpdateExpandedStatus,
  ) {
    const cards = getState().cards
      .map( card => {
        if(card.name === name) {
          card.expanded = expanded;
        }
        return card;
      });
    this.setCardsStorage(cards);
  }

  private allCardNames() {
    return Object.keys(this.cardConfig);
  }

  private defaultCardsConfig(params) {
    return this.allCardNames().reduce((aggregate: BorrowerSidebarCard[], cur) => {
      const cardConfig = this.cardConfig[cur];
      return [
        ...aggregate,
        {
          name: cur,
          shouldShow: cardConfig?.showCondition(params),
          expanded: cardConfig?.expandCondition(params),
          previewIcon: cardConfig?.previewIcon,
        },
      ];
    }, []);
  }

  private configureCards(params, cards) {
    return cards.map( card => {
      const cardConfig = this.cardConfig[card.name];
      card.shouldShow = cardConfig?.showCondition(params) || false;
      card.previewIcon = cardConfig?.previewIcon || '';
      return card;
    });
  }

  private setCardsStorage(cards) {
    localStorage.setItem('borrowerSidebarCards', JSON.stringify(cards));
  }

}
